import { motion, useMotionValue } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import {
  FLOATING_WIDGET_LAYOUT_ID,
  FAB_DEFAULT_RIGHT_CLASS,
  FAB_IDLE_BORDER_RADIUS,
  PANEL_DEFAULT_BOTTOM,
  PANEL_DEFAULT_RIGHT,
  PANEL_VIEWPORT_PADDING,
  floatingWidgetExitTransition,
  floatingWidgetMorphSpring,
  type PanelPosition,
} from "@/components/VibeChat/widgets/FloatingWidget/panel-constants";
import { cn } from "@/lib/utils";

interface FloatingFabProps {
  panelPosition: PanelPosition;
  setPanelPosition: (position: PanelPosition) => void;
  onClick: () => void;
  isActive?: boolean;
}

const FAB_SIZE = 48;
const INTENTIONAL_CLICK_MAX_MS = 200;

const subtleBob = {
  y: [0, -2, 0, 1, 0],
  rotate: [0, -3, 0, 3, 0],
};

const subtleBobTransition = {
  duration: 2.5,
  ease: "easeInOut" as const,
  repeat: Infinity,
  repeatType: "loop" as const,
};

const computeBounds = () => ({
  left: -(window.innerWidth - FAB_SIZE - PANEL_DEFAULT_RIGHT - PANEL_VIEWPORT_PADDING),
  right: PANEL_DEFAULT_RIGHT - PANEL_VIEWPORT_PADDING,
  top: -(window.innerHeight - FAB_SIZE - PANEL_DEFAULT_BOTTOM - PANEL_VIEWPORT_PADDING),
  bottom: PANEL_DEFAULT_BOTTOM - PANEL_VIEWPORT_PADDING,
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const FloatingFab = ({
  panelPosition,
  setPanelPosition,
  onClick,
  isActive = false,
}: FloatingFabProps) => {
  const savedPosition = panelPosition;
  const [bounds, setBounds] = useState(() => computeBounds());
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(clamp(savedPosition.x, bounds.left, bounds.right));
  const y = useMotionValue(clamp(savedPosition.y, bounds.top, bounds.bottom));

  const pressStartTimeRef = useRef(0);
  const didDragRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      const next = computeBounds();
      setBounds(next);
      const clampedX = clamp(x.get(), next.left, next.right);
      const clampedY = clamp(y.get(), next.top, next.bottom);
      if (clampedX !== x.get() || clampedY !== y.get()) {
        x.set(clampedX);
        y.set(clampedY);
        setPanelPosition({ x: Math.round(clampedX), y: Math.round(clampedY) });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setPanelPosition, x, y]);

  useEffect(() => {
    x.set(clamp(savedPosition.x, bounds.left, bounds.right));
    y.set(clamp(savedPosition.y, bounds.top, bounds.bottom));
  }, [
    bounds.bottom,
    bounds.left,
    bounds.right,
    bounds.top,
    savedPosition.x,
    savedPosition.y,
    x,
    y,
  ]);

  const handleFabPointerDown = () => {
    pressStartTimeRef.current = performance.now();
    didDragRef.current = false;
  };

  const handleFabPointerUp = () => {
    if (didDragRef.current) {
      return;
    }
    const elapsed = performance.now() - pressStartTimeRef.current;
    if (elapsed > INTENTIONAL_CLICK_MAX_MS) {
      return;
    }
    onClick();
  };

  return (
    <motion.div
      drag
      dragConstraints={bounds}
      dragElastic={0.1}
      dragMomentum={false}
      onDragStart={() => {
        setIsDragging(true);
        didDragRef.current = true;
      }}
      onDragEnd={() => {
        setIsDragging(false);
        setPanelPosition({ x: Math.round(x.get()), y: Math.round(y.get()) });
      }}
      style={{
        x,
        y,
      }}
      className={cn(
        "fixed bottom-5 z-50",
        FAB_DEFAULT_RIGHT_CLASS,
        "cursor-pointer active:cursor-grabbing",
        "touch-none select-none",
        isDragging && "cursor-grabbing",
      )}
    >
      <motion.button
        type="button"
        layoutId={FLOATING_WIDGET_LAYOUT_ID}
        initial={{ opacity: 0, scale: 0.3 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.3, transition: floatingWidgetExitTransition }}
        whileTap={{ scale: 0.95 }}
        onPointerDown={handleFabPointerDown}
        onPointerUp={handleFabPointerUp}
        style={{
          ...(isActive ? {} : { borderRadius: FAB_IDLE_BORDER_RADIUS }),
        }}
        transition={floatingWidgetMorphSpring}
        className={cn(
          "flex h-[48px] w-[48px] items-center justify-center overflow-hidden border border-gray-100 bg-white text-black shadow-lg hover:bg-gray-100",
          isActive && "animate-blob-morph",
        )}
      >
        <motion.span
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{ duration: 0.1, ease: "easeOut" }}
          className="relative flex items-center justify-center"
        >
          <motion.span
            animate={isActive && !isDragging ? subtleBob : undefined}
            transition={isActive && !isDragging ? subtleBobTransition : undefined}
            className="relative flex items-center justify-center"
          >
            <img
              src="/wrangler-ai-icon.png"
              alt="Wrangler AI"
              className="pointer-events-none h-10 w-10 select-none object-contain"
              draggable={false}
            />
          </motion.span>
        </motion.span>
      </motion.button>
    </motion.div>
  );
};
